import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiUsers, FiLayers, FiBarChart2, FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import LevelGenerator from '../components/admin/LevelGenerator';
import UserList from '../components/admin/UserList';
import AdminStats from '../components/admin/AdminStats';

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState<'stats' | 'levels' | 'users' | 'generator'>('stats');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      try {
        const { data } = await axios.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (data.user.role === 'ADMIN') {
          setIsAdmin(true);
        } else {
          window.location.href = '/';
        }
      } catch {
        window.location.href = '/login';
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-header-left">
          <Link to="/" className="btn btn-secondary btn-icon">
            <FiArrowLeft />
          </Link>
          <h1 className="text-gradient">Admin Panel</h1>
        </div>
      </header>

      <div className="admin-container">
        <nav className="admin-tabs">
          <button
            className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            <FiBarChart2 /> Dashboard
          </button>
          <button
            className={`tab ${activeTab === 'levels' ? 'active' : ''}`}
            onClick={() => setActiveTab('levels')}
          >
            <FiLayers /> Levels
          </button>
          <button
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <FiUsers /> Users
          </button>
          <button
            className={`tab ${activeTab === 'generator' ? 'active' : ''}`}
            onClick={() => setActiveTab('generator')}
          >
            <FiPlus /> Level Generator
          </button>
        </nav>

        <main className="admin-content">
          {activeTab === 'stats' && <AdminStats />}
          {activeTab === 'levels' && <LevelList />}
          {activeTab === 'users' && <UserList />}
          {activeTab === 'generator' && <LevelGenerator />}
        </main>
      </div>

      <style>{`
        .admin-page {
          min-height: 100vh;
          background: var(--bg-primary);
        }
        .admin-header {
          background: var(--bg-card);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--border-color);
          padding: var(--spacing-md) var(--spacing-lg);
        }
        .admin-header-left {
          display: flex;
          align-items: center;
          gap: var(--spacing-lg);
        }
        .admin-header h1 {
          font-size: 1.5rem;
        }
        .admin-container {
          display: flex;
          min-height: calc(100vh - 70px);
        }
        .admin-tabs {
          width: 220px;
          background: var(--bg-secondary);
          padding: var(--spacing-md);
          display: flex;
          flex-direction: column;
          gap: var(--spacing-xs);
        }
        .tab {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md);
          background: transparent;
          border: none;
          color: var(--text-secondary);
          font-family: inherit;
          font-size: 1rem;
          font-weight: 500;
          text-align: left;
          cursor: pointer;
          border-radius: var(--radius-md);
          transition: all var(--transition-fast);
        }
        .tab:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }
        .tab.active {
          background: var(--gradient-primary);
          color: white;
        }
        .admin-content {
          flex: 1;
          padding: var(--spacing-xl);
          overflow-y: auto;
        }
        .loading {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          color: var(--text-secondary);
        }
        @media (max-width: 768px) {
          .admin-container {
            flex-direction: column;
          }
          .admin-tabs {
            width: 100%;
            flex-direction: row;
            overflow-x: auto;
          }
          .tab {
            white-space: nowrap;
          }
        }
      `}</style>
    </div>
  );
};

// Level List Component
const LevelList = () => {
  const [levels, setLevels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/admin/levels', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLevels(data.levels);
    } catch (error) {
      console.error('Failed to fetch levels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this level?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/levels/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLevels();
    } catch (error) {
      console.error('Failed to delete level:', error);
    }
  };

  if (loading) return <div>Loading levels...</div>;

  return (
    <div className="level-list">
      <h2>Manage Levels</h2>
      <table className="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Grid</th>
            <th>Gems</th>
            <th>Moves</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {levels.map(level => (
            <tr key={level._id}>
              <td>{level.levelNumber}</td>
              <td>{level.name}</td>
              <td>{level.gridSize.width}x{level.gridSize.height}</td>
              <td>{level.gemTypes}</td>
              <td>{level.moveLimit || '∞'}</td>
              <td>
                <span className={`status-badge ${level.isActive ? 'active' : 'inactive'}`}>
                  {level.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="actions">
                <button className="btn-icon" title="Edit">
                  <FiEdit2 />
                </button>
                <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(level._id)}>
                  <FiTrash2 />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <style>{`
        .level-list h2 {
          margin-bottom: var(--spacing-lg);
        }
        .data-table {
          width: 100%;
          border-collapse: collapse;
          background: var(--bg-card);
          border-radius: var(--radius-md);
          overflow: hidden;
        }
        .data-table th,
        .data-table td {
          padding: var(--spacing-md);
          text-align: left;
          border-bottom: 1px solid var(--border-color);
        }
        .data-table th {
          background: var(--bg-tertiary);
          font-weight: 600;
          color: var(--text-secondary);
        }
        .status-badge {
          padding: var(--spacing-xs) var(--spacing-sm);
          border-radius: var(--radius-full);
          font-size: 0.75rem;
          font-weight: 600;
        }
        .status-badge.active {
          background: rgba(8, 217, 214, 0.2);
          color: var(--neon-green);
        }
        .status-badge.inactive {
          background: rgba(255, 46, 99, 0.2);
          color: var(--neon-pink);
        }
        .actions {
          display: flex;
          gap: var(--spacing-sm);
        }
        .btn-icon {
          padding: var(--spacing-sm);
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          border-radius: var(--radius-sm);
          transition: all var(--transition-fast);
        }
        .btn-icon:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }
        .btn-icon.danger:hover {
          color: var(--neon-pink);
        }
      `}</style>
    </div>
  );
};

export default AdminPage;
